import { useState, useEffect } from "react";
import { SurveyServices } from "../services/SurveyServices";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { CircularProgress } from "@mui/material";
import { handleApiErrorResponses } from "../utils/ApisErrorHandling";
import { useSearchParams } from "react-router-dom";

const DeactivateAccountForm = () => {
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("id");

  // ✅ Validation Schema
  const validationSchema = Yup.object().shape({
    confirmation: Yup.string().required("Please select Yes or No"),

    reason: Yup.string().when("confirmation", {
      is: (val) => val === "Yes",
      then: (schema) => schema.required("Please select a reason"),
      otherwise: (schema) => schema.notRequired(),
    }),

    otherReason: Yup.string().when(["confirmation", "reason"], {
      is: (confirmation, reason) =>
        confirmation === "Yes" && reason === "Other",
      then: (schema) => schema.required("Please enter the reason"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  // ✅ Check backend status when component loads
  useEffect(() => {
    const checkRequestStatus = async () => {
      if (!requestId) {
        setCheckingStatus(false);
        return;
      }

      try {
        const res = await SurveyServices.getDeleteUserRequest(requestId);

        const request = res?.data?.requests?.[0];

        //  If backend sends record → already submitted
        if (request?.status === "APPROVED" || request?.status === "REJECTED") {
          setSubmitted(true);
        }
      } catch (err) {
        
        
          handleApiErrorResponses(err);
       
      }

      setCheckingStatus(false);
    };

    checkRequestStatus();
  }, [requestId]);

  // ✅ Submit handler
  const handleSave = async (values, { resetForm }) => {
    if (submitted) return;

    if (!requestId) {
      alert("Invalid request. ID not found.");
      return;
    }

    setSaving(true);

    const finalReason =
      values.reason === "Other" ? values.otherReason : values.reason;

    const status = values.confirmation === "Yes" ? "APPROVED" : "REJECTED";

    const data = {
      reason: finalReason || "",
      status,
    };

    try {
      const response = await SurveyServices.updateDeleteUserRequest(
        requestId,
        data,
      );

      alert(response.data.message);

      resetForm();

      // 🔥 Lock form after successful submit
      setSubmitted(true);
    } catch (err) {
      handleApiErrorResponses(err);
    }

    setSaving(false);
  };

  // ✅ Loader while checking request status
  if (checkingStatus) {
    return (
      <div className="flex justify-center mt-10">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      <div className="text-[40px] lg:text-[50px] font-medium -tracking-[7%]">
        Request Account Deactivation
      </div>

      <div className="mt-7 lg:mt-[50px]">
        <Formik
          initialValues={{
            confirmation: "",
            reason: "",
            otherReason: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSave}
        >
          {({ values }) => (
            <Form>
              {/* Question 1 */}
              <div className="mb-6 p-4 lg:p-8 rounded-[20px] bg-[#FFFFFFB3]">
                <div className="text-base lg:text-xl -tracking-[7%]">
                  Are you sure you want to delete this account?
                </div>

                <div
                  style={{ border: "1px solid #E8E8E8", marginBlock: "16px" }}
                />

                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2">
                    <Field
                      type="radio"
                      name="confirmation"
                      value="Yes"
                      disabled={submitted}
                    />
                    Yes
                  </label>

                  <label className="flex items-center gap-2">
                    <Field
                      type="radio"
                      name="confirmation"
                      value="No"
                      disabled={submitted}
                    />
                    No
                  </label>
                </div>

                <ErrorMessage
                  name="confirmation"
                  component="div"
                  style={{ color: "red", marginTop: "4px" }}
                />
              </div>

              {/* Reason Section */}
              {values.confirmation === "Yes" && (
                <div className="mb-6 p-4 lg:p-8 rounded-[20px] bg-[#FFFFFFB3]">
                  <div className="text-base lg:text-xl -tracking-[7%]">
                    Please select the reason
                  </div>

                  <div
                    style={{
                      border: "1px solid #E8E8E8",
                      marginBlock: "16px",
                    }}
                  />

                  <Field
                    as="select"
                    name="reason"
                    disabled={submitted}
                    className="p-3 rounded-lg border border-gray-300 w-full sm:w-3/4 md:w-1/2 lg:w-1/3"
                  >
                    <option value="">Select a reason</option>
                    <option value="No longer required">
                      No longer required
                    </option>
                    <option value="Unsatisfied with service">
                      Unsatisfied with service
                    </option>
                    <option value="Data privacy concerns">
                      Data privacy concerns
                    </option>
                    <option value="Other">Other</option>
                  </Field>

                  <ErrorMessage
                    name="reason"
                    component="div"
                    style={{ color: "red", marginTop: "4px" }}
                  />

                  {values.reason === "Other" && (
                    <div className="mt-4">
                      <Field
                        as="textarea"
                        name="otherReason"
                        disabled={submitted}
                        placeholder="Enter your reason"
                        className="w-full p-3 rounded-lg border border-gray-300"
                      />
                      <ErrorMessage
                        name="otherReason"
                        component="div"
                        style={{ color: "red", marginTop: "4px" }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-center items-center">
                <button
                  type="submit"
                  disabled={saving || submitted}
                  className={`flex items-center justify-center gap-2 text-white font-semibold uppercase px-8 py-3 rounded-full transition-all ${
                    submitted
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-[#017C71]"
                  }`}
                >
                  {saving && (
                    <CircularProgress
                      size={18}
                      thickness={5}
                      style={{ color: "white" }}
                    />
                  )}

                  {submitted
                    ? "Submitted"
                    : saving
                      ? "Submitting..."
                      : "Submit"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default DeactivateAccountForm;
